/*
 * Datazoom, Inc ("COMPANY") CONFIDENTIAL
 * Copyright (c) 2017-2018 [Datazoom, Inc.], All Rights Reserved.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

import { routes } from "../utilities/routes";
import { PrivateRoute, PublicRoute } from "./RouteComponents";
import React, { lazy } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import { Switch } from "react-router-dom";
import Loader from "../common/Loader";
import Header from "../common/Header";
import Footer from "../common/Footer";
import Notfound from "../common/Notfound";
import Notification from "../common/Notification";
import SkeletonContainer from "../../app/common/Skeleton/SkeletonContainer/SkeletonContainer";

const LoadableLogin = lazy(() => import("../Login"));
const LoadableHome = lazy(() => import("../Home"));
const LoadableForgotPassword = lazy(() => import("../ForgotPassword"));
const LoadableChangePassword = lazy(() => import("../ChangePassword"));
const LoadableResetPassword = lazy(() => import("../ResetPassword"));
const LoadableResetPasswordSuccess = lazy(() =>
  import("../ResetPassword/ResetPasswordSuccess"),
);
const LoadableCollectorList = lazy(() =>
  import("../../app/Collector/CollectorList"),
);
const LoadableConnectorList = lazy(() =>
  import("../../app/Connector/ConnectorList/index"),
);
const LoadableConnectorListV2 = lazy(() =>
  import("../../app/Connector/ConnectorList/indexV2"),
);
const LoadableDatapipeList = lazy(() =>
  import("../../app/Datapipe/DatapipeList/DatapipeListContainer"),
);
const LoadableCreateCollector = lazy(() =>
  import("../../app/Collector/CreateCollector/CreateCollectorContainer"),
);

const LoadableCreateConnector = lazy(() =>
  import("../../app/Connector/CreateConnector"),
);
const LoadableCreateDatapipe = lazy(() => import("../Datapipe/CreateDatapipe"));
const LoadableViewDatapipe = lazy(() => import("../Datapipe/ViewDatapipe"));
const LoadableDatapipe = lazy(() =>
  import("../../app/Datapipe/DatapipeContainer/DatapipeContainer"),
);
const LoadableAdminSettings = lazy(() => import("../AdminSettings"));
const LoadableLogout = lazy(() => import("../Logout/Logout"));
const LoadableSignup = lazy(() => import("../Signup"));
const LoadableMediaTailorSignup = lazy(() =>
  import("../MediaTailorSignup/MediaTailorSignupContainer"),
);
const LoadableCompleteSignup = lazy(() => import("../Signup/CompleteSignup"));
const LoadableMediaTailorRegistration = lazy(() =>
  import("../MediaTailorSignup/Registration/MediaTailorRegContainer"),
);
const LoadablePendingApproval = lazy(() =>
  import("../Signup/CompleteSignup/SignUpRequestSubmitted"),
);
const LoadableRateCard = lazy(() => import("../../app/PlatformSettings/index"));
const LoadablePlatformSettings = lazy(() =>
  import("../../app-old/PlatformSettings/index"),
);
const LoadablePaymentTakeOver = lazy(() =>
  import("../PaymentTakeOver/PaymentTakeOver"),
);
const LoadableDelete = lazy(() =>
  import("../common/DeleteComponent/DeleteContainer"),
);
const LoadableCustomData = lazy(() => import("../../app/CustomData/index"));
const LoadableCompanies = lazy(() => import("../../app/Company/index"));
const LoadableUsers = lazy(() => import("../../app/User/index"));
const LoadableProjects = lazy(() => import("../../app/Projects/index"));
const LoadableCollectorConfigContainerSdk = lazy(() =>
  import(
    "../../app/Collector/CollectorConfigurationSdk/CollectorConfigSdkContainer"
  ),
);
const LoadableConnectorConfigContainer = lazy(() =>
  import("../../app/Connector/ConnectorConfiguration/ConnectorConfigContainer"),
);
const LoadableCollectorManagmentContainer = lazy(() =>
  import("../../app/CollectorManagement/CollectorManagementContainer"),
);
const LoadableDataDictionayV2 = lazy(() =>
  import("../../app/DataDictionaryV2/index"),
);
const LoadableDatapipeDetails = lazy(() =>
  import("../../app/Datapipe/DatapipeContainer/DatapipeContainer"),
);

export default class Main extends React.Component {
  /* istanbul ignore next */
  render() {
    return (
      <div className="wrapper">
        <Header />
        <Notification />
        <div id="modal-root" />
        <div id="createColBox" />
        <Switch>
          <PublicRoute
            exact
            path="/"
            component={LoadableLogin}
            loader={<Loader />}
          />
          <PublicRoute
            exact
            path={routes.forgot_password_page}
            component={LoadableForgotPassword}
            loader={<Loader />}
          />
          <PublicRoute
            path={routes.reset_password_page}
            component={LoadableResetPassword}
            loader={<Loader />}
          />
          <PublicRoute
            path={routes.reset_password_success}
            component={LoadableResetPasswordSuccess}
            loader={<Loader />}
          />
          <PublicRoute
            exact
            path={routes.login}
            component={LoadableLogin}
            loader={<Loader />}
          />
          {process.env.REACT_APP_MEDIA_TAILOR === "true" && (
            <PublicRoute
              exact
              path={routes.mediatailor_signup}
              component={LoadableMediaTailorSignup}
              loader={<Loader />}
            />
          )}
          <PublicRoute
            exact
            path={routes.signup}
            component={LoadableSignup}
            loader={<Loader />}
          />
          <PublicRoute
            exact
            path={routes.complete_signup}
            component={LoadableCompleteSignup}
            loader={<Loader />}
          />
          {process.env.REACT_APP_MEDIA_TAILOR === "true" && (
            <PublicRoute
              exact
              path={routes.mediatailor_register}
              component={LoadableMediaTailorRegistration}
              loader={<Loader />}
            />
          )}
          <PublicRoute
            exact
            path={routes.pending_approval}
            component={LoadablePendingApproval}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.change_password}
            component={LoadableChangePassword}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.dashboard}
            component={LoadableHome}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.collector_list}
            component={LoadableCollectorList}
            loader={<SkeletonContainer header search />}
          />
          <PrivateRoute
            exact
            path={routes.connector_list}
            component={LoadableConnectorListV2}
            loader={<SkeletonContainer header search />}
          />
          <PrivateRoute
            exact
            path={routes.datapipe_list}
            component={LoadableDatapipeList}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.data_pipe_details}
            component={LoadableDatapipeDetails}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.create_collector}
            component={LoadableCreateCollector}
            loader={
              <SkeletonContainer
                appBar
                stepper
                count={3}
                search
                playerList
                collector
                mt="187px"
              />
            }
          />
          <PrivateRoute
            exact
            path={routes.create_connector}
            component={LoadableCreateConnector}
            loader={
              <SkeletonContainer
                appBar
                stepper
                count={4}
                search
                playerList
                mt="187px"
              />
            }
          />
          <PrivateRoute
            exact
            path={routes.view_pipe}
            component={LoadableViewDatapipe}
            loader={<Loader />}
          />

          <PrivateRoute
            exact
            path={routes.create_pipe}
            component={LoadableCreateDatapipe}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.create_datapipe}
            component={LoadableDatapipe}
            loader={<Loader />}
          />

          <PrivateRoute
            exact
            path={routes.admin_settings}
            component={LoadableAdminSettings}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.logout}
            component={LoadableLogout}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.edit_pipe}
            component={LoadableCreateDatapipe}
            loader={<Loader />}
          />

          <PrivateRoute
            exact
            path={routes.rate_cards}
            component={LoadableRateCard}
            loader={<SkeletonContainer header search ratesList />}
          />

          <PrivateRoute
            exact
            path={routes.platform_settings}
            component={LoadablePlatformSettings}
            loader={<Loader />}
          />

          <PrivateRoute
            exact
            path={routes.payment}
            component={LoadablePaymentTakeOver}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.delete_connector}
            component={LoadableDelete}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.delete_collector}
            component={LoadableDelete}
            loader={<Loader />}
          />
          <PrivateRoute
            exact
            path={routes.delete_datapipe}
            component={LoadableDelete}
            loader={<Loader />}
          />
          <PrivateRoute
            path={routes.data_dictionary}
            component={LoadableDataDictionayV2}
            loader={<SkeletonContainer header search list />}
          />
          <PrivateRoute
            exact
            path={routes.custom_data}
            component={LoadableCustomData}
            loader={<SkeletonContainer header search list />}
          />
          <PrivateRoute
            exact
            path={routes.companies}
            component={LoadableCompanies}
            loader={<SkeletonContainer header search list />}
          />
          <PrivateRoute
            exact
            path={routes.users}
            component={LoadableUsers}
            loader={<SkeletonContainer header search list />}
          />
          <PrivateRoute
            exact
            path={routes.projects}
            component={LoadableProjects}
            loader={
              <SkeletonContainer
                header
                search={false}
                list={false}
                landingPage
              />
            }
          />
          <PrivateRoute
            exact
            path={routes.collectorConfiguration}
            component={LoadableCollectorConfigContainerSdk}
            loader={<SkeletonContainer config verticalTab details />}
          />
          <PrivateRoute
            exact
            path={routes.connectorConfiguration}
            component={LoadableConnectorConfigContainer}
            loader={<SkeletonContainer config verticalTab details />}
          />
          <PrivateRoute
            exact
            path={routes.collector_management}
            component={LoadableCollectorManagmentContainer}
            loader={<SkeletonContainer header search list />}
          />
          <PrivateRoute exact path="*" component={Notfound} />
          <PublicRoute path="*" component={Notfound} />
        </Switch>
        {
          // Here we can have any sort of style with style attribute given,
          //refer https://www.npmjs.com/package/react-toastify/v/1.4.3
        }
        <ToastContainer
          autoClose={3000}
          closeButton={false}
          hideProgressBar={true}
          className="toastCont"
          position={toast.POSITION.BOTTOM_LEFT}
        />
        <Footer />
      </div>
    );
  }
}

// We cannot connect main with redux since it would override the default shouldComponentUpdate
// Main with <Switch> should be a location aware component which would re render if location changes
// it wont be happening if connected to store
// another solution would be bring <Route> instead of <PublicRoute> and <PrivateRoute>
// This is a nice abstraction for auth in routes. So to preserve it the auth check logic is moved to header.
// refer : https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/guides/blocked-updates.md
